import { CheckCheck, ExternalLink, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { OutcomeBadge } from '../components/imports';
import { REVIEW_KEYS, ReviewPanel } from '../components/review-panel';
import { btnGhost, btnSecondary, inputBaseCls } from '../components/ui';
import { ApiError, type CandidateRow, type ImportSource } from '../lib/api';
import { OUTCOME_LABELS, formatRange } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * Coda di revisione a due pannelli: a sinistra i candidati, a destra quello scelto affiancato
 * agli eventi simili, campo per campo, con le azioni. Dopo una decisione si passa da soli al
 * successivo; j/k (o le frecce) scorrono l'elenco. "Applica i suggerimenti" sulle righe
 * selezionate vale per nuovi, duplicati e non validi; incerti e aggiornamenti si decidono qui.
 */
export function ReviewList() {
  const { api, canEdit } = useSession();
  const [sp, setSp] = useSearchParams();
  const source = sp.get('source') ?? '';
  const outcome = sp.get('outcome') ?? '';
  const current = sp.get('id') ?? '';
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const set = useCallback(
    (patch: Record<string, string | undefined>) =>
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
          return next;
        },
        { replace: true },
      ),
    [setSp],
  );

  const load = async (after?: string) => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams({ status: 'needs_review', limit: '100' });
      if (source) p.set('source', source);
      if (outcome) p.set('outcome', outcome);
      if (after) p.set('cursor', after);
      const r = await api<{ items: CandidateRow[]; cursor?: string }>(`/imports/candidates?${p}`);
      // dal primo giorno: si rivede nell'ordine del calendario
      const items = r.items.sort(
        (a, b) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title),
      );
      setRows((prev) => (after ? [...prev, ...items] : items));
      setCursor(r.cursor);
      if (!after) {
        setSelected(new Set());
        if (!items.some((x) => x.id === current)) set({ id: items[0]?.id });
      }
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canEdit) return;
    void load();
    api<{ sources: ImportSource[] }>('/imports/sources')
      .then((r) => setSources(r.sources))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, source, outcome]);

  const open = rows.filter((r) => r.status === 'needs_review');
  const index = rows.findIndex((r) => r.id === current);

  /** Il prossimo da decidere dopo `id` (o il precedente, se è l'ultimo). */
  const nextOpen = (id: string, list: CandidateRow[]) => {
    const i = list.findIndex((r) => r.id === id);
    const after = list.slice(i + 1).find((r) => r.status === 'needs_review');
    const before = [...list.slice(0, i)].reverse().find((r) => r.status === 'needs_review');
    return (after ?? before)?.id;
  };

  const onDecided = useCallback(
    (row: CandidateRow) => {
      const next = rows.map((r) => (r.id === row.id ? row : r));
      setRows(next);
      set({ id: nextOpen(row.id, next) ?? row.id });
      setSelected((prev) => {
        const s = new Set(prev);
        s.delete(row.id);
        return s;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, set],
  );

  // j/k e frecce per scorrere l'elenco
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && ['INPUT', 'SELECT', 'TEXTAREA'].includes(t.tagName)) return;
      const step =
        e.key === 'j' || e.key === 'ArrowDown' ? 1 : e.key === 'k' || e.key === 'ArrowUp' ? -1 : 0;
      if (!step || rows.length === 0) return;
      e.preventDefault();
      const i = Math.min(rows.length - 1, Math.max(0, (index < 0 ? 0 : index) + step));
      set({ id: rows[i]!.id });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, index, set]);

  // la riga scelta resta visibile nell'elenco
  useEffect(() => {
    listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const bulk = async (action: 'suggested' | 'reject') => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (action === 'reject' && !window.confirm(`Scartare ${ids.length} candidati?`)) return;
    setLoading(true);
    setNotice(null);
    setError(null);
    // a blocchi: l'API ne accetta al massimo 100 per richiesta e risponde entro 29 secondi
    const CHUNK = 20;
    let ok = 0;
    let skipped = 0;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        setNotice(`Applico… ${i} di ${ids.length}`);
        const r = await api<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
          '/imports/candidates/bulk',
          { method: 'POST', body: { action, ids: ids.slice(i, i + CHUNK) } },
        );
        ok += r.results.filter((x) => x.ok).length;
        skipped += r.results.filter((x) => !x.ok).length;
      }
      setNotice(
        `${ok} applicati${skipped ? `, ${skipped} lasciati da decidere (incerti, aggiornamenti o già decisi)` : ''}.`,
      );
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setError(`${msg}. Applicati finora: ${ok}; gli altri restano in coda, puoi riprovare.`);
    } finally {
      await load();
      setLoading(false);
    }
  };

  const sourceName = (id: string) => sources.find((s) => s.id === id)?.name ?? id;
  const allOn = open.length > 0 && open.every((r) => selected.has(r.id));

  return (
    <main className="mx-auto max-w-[1600px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Coda di revisione</h1>
        <span className="text-sm text-muted">
          {loading ? 'Caricamento…' : `${open.length} da rivedere`}
        </span>
        <button onClick={() => void load()} className={`${btnGhost} ml-auto`}>
          <RefreshCw className="size-4" aria-hidden /> Aggiorna
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border border-surface-strong bg-surface p-3">
        <select
          value={source}
          onChange={(e) => set({ source: e.target.value || undefined, id: undefined })}
          className={inputBaseCls}
          aria-label="Sorgente"
        >
          <option value="">Tutte le sorgenti</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Esito">
          {['', 'new', 'duplicate', 'update', 'uncertain'].map((o) => (
            <button
              key={o || 'all'}
              aria-pressed={outcome === o}
              onClick={() => set({ outcome: o || undefined, id: undefined })}
              className={`h-9 px-3 text-sm ${outcome === o ? 'bg-fg font-medium text-page' : 'bg-page hover:bg-surface-strong'}`}
            >
              {o ? OUTCOME_LABELS[o]!.label : 'Tutti'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            onClick={() => void bulk('suggested')}
            disabled={selected.size === 0 || loading}
            className={btnSecondary}
            title="Nuovi: crea e pubblica · Duplicati: unisci tenendo l’evento (completato con la sorgente) · Gli altri restano da decidere"
          >
            <CheckCheck className="size-4" aria-hidden /> Applica i suggerimenti ({selected.size})
          </button>
          <button
            onClick={() => void bulk('reject')}
            disabled={selected.size === 0 || loading}
            className={btnGhost}
          >
            <X className="size-4" aria-hidden /> Scarta
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-surface p-3 text-sm">
          {notice}
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(300px,2fr)_minmax(0,5fr)]">
        {/* elenco */}
        <section className="border border-surface-strong lg:max-h-[calc(100dvh-14rem)] lg:overflow-y-auto">
          <label className="flex items-center gap-2 border-b border-surface-strong bg-surface px-3 py-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={allOn}
              onChange={(e) => open.forEach((r) => toggle(r.id, e.target.checked))}
            />
            Seleziona tutti quelli da rivedere
          </label>
          <ul ref={listRef}>
            {rows.map((r) => {
              const active = r.id === current;
              const done = r.status !== 'needs_review';
              return (
                <li
                  key={r.id}
                  aria-current={active}
                  className={`flex gap-2 border-b border-surface-strong px-3 py-2 text-sm ${active ? 'bg-surface-strong' : 'hover:bg-surface'} ${done ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    aria-label={`Seleziona ${r.title}`}
                    disabled={done}
                    checked={selected.has(r.id)}
                    onChange={(e) => toggle(r.id, e.target.checked)}
                  />
                  <button
                    onClick={() => set({ id: r.id })}
                    className="flex min-w-0 flex-1 flex-col items-start text-left"
                  >
                    <span className="w-full truncate font-medium">{r.title}</span>
                    <span className="text-xs text-muted">
                      {formatRange(r.startDate, r.endDate)} · {r.comune}
                      {!source && ` · ${sourceName(r.sourceId)}`}
                    </span>
                    <span className="mt-1 flex items-center gap-2">
                      <OutcomeBadge outcome={r.outcome} />
                      {done && <span className="text-xs text-muted">deciso</span>}
                      {r.match && !done && r.outcome !== 'new' && (
                        <span className="truncate text-xs text-muted">
                          ≈ {r.match.title} ({r.match.score.toFixed(2).replace('.', ',')})
                        </span>
                      )}
                    </span>
                  </button>
                  {r.sourceUrl && (
                    <a
                      href={r.sourceUrl}
                      target="_blank"
                      rel="noopener"
                      title="Apri la pagina della sorgente"
                      className="shrink-0 self-start p-1 text-muted hover:text-fg"
                    >
                      <ExternalLink className="size-4" aria-hidden />
                      <span className="sr-only">Pagina della sorgente</span>
                    </a>
                  )}
                </li>
              );
            })}
            {rows.length === 0 && !loading && (
              <li className="px-3 py-4 text-sm text-muted">Niente da rivedere.</li>
            )}
          </ul>
          {cursor && !loading && (
            <button onClick={() => void load(cursor)} className={`${btnGhost} m-2`}>
              Carica altri
            </button>
          )}
        </section>

        {/* confronto */}
        <section className="border border-surface-strong p-4 lg:max-h-[calc(100dvh-14rem)] lg:overflow-y-auto">
          {current ? (
            <ReviewPanel key={current} id={current} onDecided={onDecided} />
          ) : (
            <p className="text-sm text-muted">
              Scegli un candidato dall’elenco.
              {/* scorciatoie da tastiera: solo da computer */}
              <span className="hidden md:inline"> {REVIEW_KEYS}</span>
            </p>
          )}
        </section>
      </div>
    </main>
  );
}
